import type { components } from "../api/schema.gen";

/**
 * Crew labels (db/README Revision 104, contract 0.10.64): how a role is SHOWN. The words, ranks and position rule come only
 * from the server (`GET /crew-labels`); this file holds the few lines that apply them, never a copy of the table. Every role
 * key stays as it is, so access checks never read a label.
 *
 * Revision 107 (contract 0.10.67, the ATC group): the server lists the roles in DISPLAY order — the flight crew by rank, then
 * the ATC group ("ATC (Admin)", "ATC (DevOps)", "ATC (Infra)", "ATC (PMO)"; no rank), then System Admin — and each row says its
 * `group`. Every ordered list follows the table's order and puts a divider before the ATC group.
 */
export type CrewLabelTable = components["schemas"]["CrewLabelTable"];
export type CrewRoleRow = CrewLabelTable["roles"][number];
export type CrewGroup = CrewRoleRow["group"];

/**
 * The server's position normalisation (`CrewLabels.NormalisePosition`): lower case, every run of characters that are not
 * letters, digits or combining marks turned into one space, trimmed. Combining marks stay, so a Thai word is never split.
 */
export function normalisePosition(position: string | null | undefined): string {
  if (!position) return "";
  return position.toLowerCase().replace(/[^\p{L}\p{N}\p{M}]+/gu, " ").trim();
}

/** The Cabin Crew type of a HumanOS position: the first rule with a keyword matching whole words; null when none does. */
export function positionTag(table: CrewLabelTable, position: string | null | undefined): string | null {
  const text = normalisePosition(position);
  if (!text) return null;
  const padded = ` ${text} `;
  for (const rule of table.positionTags) {
    if (rule.keywords.some((keyword) => padded.includes(` ${keyword} `))) return rule.tag;
  }
  return null;
}

/** The table's row for a role key; undefined for a key it does not know. */
export function crewRow(table: CrewLabelTable, role: string): CrewRoleRow | undefined {
  return table.roles.find((row) => row.role === role);
}

/** The label of a role key ("Capt."); an unknown key is shown as itself. */
export function roleLabel(table: CrewLabelTable, role: string): string {
  return crewRow(table, role)?.label ?? role;
}

/** What a role picker, matrix column or rule shows ("Cabin Crew · Analyst"); an unknown key is shown as itself. */
export function rolePickerLabel(table: CrewLabelTable, role: string): string {
  return crewRow(table, role)?.pickerLabel ?? role;
}

/** A role key's place in display order (the server's table order); an unknown key sorts last. */
export function roleOrder(table: CrewLabelTable, role: string): number {
  const at = table.roles.findIndex((row) => row.role === role);
  return at < 0 ? Number.MAX_SAFE_INTEGER : at;
}

/** The group of a role key (`flight`, `atc`, `system`); null for a key the table does not know. */
export function roleGroup(table: CrewLabelTable, role: string): CrewGroup | null {
  return crewRow(table, role)?.group ?? null;
}

/** True for a role of the ATC ground group (no rank, listed apart). */
export function isAtcRole(table: CrewLabelTable, role: string): boolean {
  return roleGroup(table, role) === "atc";
}

/**
 * How a person holding `roles` is shown: the label of their first role in display order (a flight crew role before ATC, ATC
 * before System Admin), and for a Cabin Crew role the type from their position ("Cabin Crew (Dev)"). Null when they hold no role
 * the table knows. Same rule as `CrewLabels.For`.
 */
export function personCrewLabel(table: CrewLabelTable, roles: readonly string[], position?: string | null): string | null {
  let best: CrewRoleRow | undefined;
  for (const role of roles) {
    const row = crewRow(table, role);
    if (row && (!best || roleOrder(table, row.role) < roleOrder(table, best.role))) best = row;
  }
  if (!best) return null;
  const tag = best.cabinCrew ? positionTag(table, position) : null;
  return tag ? `${best.label} (${tag})` : best.label;
}

/**
 * One role ASSIGNMENT of a person, as an admin list shows it (D-B14-02): the person label for that role ("Cabin Crew (SA)"),
 * and, when the label is shared by several roles, the role's own name after it ("Cabin Crew (SA) · Analyst"), so two rows
 * of one person never read the same and Remove is unambiguous. An unknown key is shown as itself.
 */
export function assignmentLabel(table: CrewLabelTable, role: string, position?: string | null): string {
  const row = crewRow(table, role);
  if (!row) return role;
  const person = personCrewLabel(table, [role], position) ?? row.label;
  return row.pickerLabel !== row.label ? `${person} · ${row.roleName}` : person;
}

/** Role keys in display order (the server's table order: the flight crew by rank, then ATC, then System Admin). */
export function sortRoles<T extends string>(table: CrewLabelTable, roles: readonly T[]): T[] {
  return [...roles].sort((a, b) => roleOrder(table, a) - roleOrder(table, b));
}

/**
 * Role keys in display order, split where a list draws its divider (Revision 107): the flight crew (and unknown keys) first, then
 * the ATC group and System Admin. A list shows `crew`, a divider when both parts are non-empty, then `ground`.
 */
export function splitRoles<T extends string>(table: CrewLabelTable, roles: readonly T[]): { crew: T[]; ground: T[] } {
  const sorted = sortRoles(table, roles);
  const ground = (role: T) => {
    const group = roleGroup(table, role);
    return group === "atc" || group === "system";
  };
  return { crew: sorted.filter((role) => !ground(role)), ground: sorted.filter(ground) };
}

/**
 * The table the app last read from the server, for the few PURE text builders (history lines, test-mode actor names, notice
 * owner lists) that cannot call a hook. Set by `useCrewLabelTable` as soon as `GET /crew-labels` answers; the shell asks for
 * it at start-up. Before that a role reads as its key. Components should prefer `useCrewLabels()`, which re-renders.
 */
let current: CrewLabelTable = { roles: [], positionTags: [] };

export function setCurrentCrewTable(table: CrewLabelTable): void {
  current = table;
}

/** `rolePickerLabel` against the table last read from the server (see `setCurrentCrewTable`). */
export function currentRoleLabel(role: string): string {
  return rolePickerLabel(current, role);
}

/** `sortRoles` against the table last read from the server (see `setCurrentCrewTable`): crew by rank, then ATC, then System Admin. */
export function currentSortRoles<T extends string>(roles: readonly T[]): T[] {
  return sortRoles(current, roles);
}

/**
 * Role assignments as a members list shows them (Revision 107): the flight crew first, in display order (then by name), and the
 * ATC group apart, by name (ATC has no rank). Each entry keeps its index in `rows`, so Remove still removes the right row.
 */
export function groupAssignments<R extends { role: string }>(
  table: CrewLabelTable,
  rows: readonly R[],
  nameOf: (row: R) => string,
): { crew: { row: R; index: number }[]; ground: { row: R; index: number }[] } {
  const collator = new Intl.Collator(["en", "th"], { sensitivity: "base", numeric: true });
  const indexed = rows.map((row, index) => ({ row, index }));
  const isGround = (role: string) => {
    const group = roleGroup(table, role);
    return group === "atc" || group === "system";
  };
  const crew = indexed
    .filter((entry) => !isGround(entry.row.role))
    .sort((a, b) => roleOrder(table, a.row.role) - roleOrder(table, b.row.role) || collator.compare(nameOf(a.row), nameOf(b.row)) || a.index - b.index);
  const ground = indexed
    .filter((entry) => isGround(entry.row.role))
    .sort((a, b) => collator.compare(nameOf(a.row), nameOf(b.row)) || roleOrder(table, a.row.role) - roleOrder(table, b.row.role) || a.index - b.index);
  return { crew, ground };
}
